// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {Redirect, useHistory, useLocation} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import {useOktaAuth} from '@okta/okta-react';
import {SET_CONFIGURATION, SET_USER_DATA} from '../../../action-list';
import {useIsJdfFlow} from '../../../services/jdf-services/jdf-hepler';
import {getHelpdeskUrl} from '../../../services/service-now-url';
import {CustomerRoutes} from '../../../literals/customer-routes';
import {HEALTH_ALLOWED_USERS} from '../../../literals/health-allowed-users';

const SALES_CENTER_APP_NAME = 'Sales Center';

const useShowNoAccessForDUA = () => {
    const digitalUserAccountSearch = useSelector(
        (state) => state.configuration.features.digitalUserAccountSearch
    );
    const canRetrieveDigitalUserDetails = useSelector(
        (state) => state.configuration.businessActivities.canRetrieveDigitalUserDetails
    );

    return digitalUserAccountSearch.isEnabled &&
        digitalUserAccountSearch.showByDefault &&
        canRetrieveDigitalUserDetails !== true;
};

const useResolvedUserId = () => {
    const {
        authState, oktaAuth
    } = useOktaAuth();
    const dispatch = useDispatch();
    const impersonateUserId = useSelector((state) => state.user.impersonateUserId);
    const [userId, setUserId] = React.useState(null);

    React.useEffect(() => {
        if (authState.isAuthenticated) {
            oktaAuth.token.getUserInfo().then((user) => {
                setUserId(!impersonateUserId ? user.userID : impersonateUserId);
                dispatch({
                    type: SET_USER_DATA,
                    user: {
                        loggedInUserId: user.userID
                    }
                });
            }).catch(() => {});
        } else {
            setUserId(impersonateUserId);
        }
    }, [authState, oktaAuth, impersonateUserId]);

    return userId;
};

const routeMap = {
    'EDIT_CUSTOMER': CustomerRoutes.EDIT_JDF_CUSTOMER,
    'ADD_CUSTOMER': CustomerRoutes.ADD_JDF_CUSTOMER
};

const useJdfRedirect = () => {
    const dispatch = useDispatch();
    const routeRequested = useSelector((state) => state.configuration.routing?.view);
    const configuration = useSelector((state) => state.configuration);
    const isJdfFlow = useIsJdfFlow();
    const history = useHistory();

    const updateConfigurationAndRedirect = React.useCallback((route) => {
        dispatch({
            configuration: {
                routing: {
                    ...configuration.routing,
                    view: route === 'EDIT_CUSTOMER' ? 'EDIT_JDF_CUSTOMER' : 'ADD_JDF_CUSTOMER'
                }
            },
            type: SET_CONFIGURATION
        });
        history.push(routeMap[route]);
    }, [dispatch, configuration.routing, history]);

    React.useEffect(() => {
        if (isJdfFlow && routeMap[routeRequested]) {
            updateConfigurationAndRedirect(routeRequested);
        }
    }, [routeRequested, isJdfFlow, updateConfigurationAndRedirect]);
};

const isHealthAllowedUser = (userId) => HEALTH_ALLOWED_USERS.includes(userId.toUpperCase());

const renderNoAccessRedirect = (userId, messageId) => userId ? <Redirect
    to={{
        pathname: '/no-access',
        state: {
            message: <FormattedMessage
                id={messageId}
                values={{
                    userId
                }}
            />
        }
    }}
/> : null;

const renderNoAccessRedirectForNonDealer = (userId) => userId ? <Redirect
    to={{
        pathname: '/no-access',
        state: {
            message: <FormattedMessage
                id='messages.USER_DOES_NOT_HAVE_DUA_ACCESS_NON_DEALER'
                values={{
                    createTicket: (
                        <a
                            href={getHelpdeskUrl()}
                            rel='noopener noreferrer'
                            target='_blank'
                        >
                            <FormattedMessage id='labels.CREATE_A_TICKET'/>
                        </a>
                    )
                }}
            />
        }
    }}
/> : null;

const resolveDuaNoAccess = (isDealer, userId) => isDealer ?
    renderNoAccessRedirect(userId, 'messages.USER_DOES_NOT_HAVE_DUA_ACCESS') :
    renderNoAccessRedirectForNonDealer(userId);

const resolveCheckHealthAccess = (userId, children) => {
    if (!userId) {
        return null;
    }

    return isHealthAllowedUser(userId) ? children : renderNoAccessRedirect(userId, 'messages.USER_CANNOT_ACCESS_CHECK_HEALTH');
};

const resolveAccessResult = (isCSC2User, parentAppName, userId, children) => {
    const isParentAppSalesCenter = parentAppName.startsWith(SALES_CENTER_APP_NAME);

    if (isParentAppSalesCenter) {
        return children;
    }

    return isCSC2User ? children : renderNoAccessRedirect(userId, 'messages.USER_CANNOT_USE_CSC');
};

const useAccessCheckState = () => {
    const isCSC2User = useSelector((state) => state.user.isCSC2User);
    const isDealer = useSelector((state) => state.user.isDealer);
    const parentAppName = useSelector((state) => state.configuration.parentAppName);
    const location = useLocation();
    const userId = useResolvedUserId();
    const showNoAccessForDUA = useShowNoAccessForDUA();

    useJdfRedirect();

    return {
        isCSC2User,
        isCheckHealthPage: location.pathname === CustomerRoutes.CHECK_HEALTH,
        isDealer,
        parentAppName,
        showNoAccessForDUA,
        userId
    };
};

export const AccessCheck = (props) => {
    const {
        isCSC2User, isCheckHealthPage, isDealer, parentAppName, showNoAccessForDUA, userId
    } = useAccessCheckState();

    if (isCheckHealthPage) {
        return resolveCheckHealthAccess(userId, props.children);
    }

    if (showNoAccessForDUA) {
        return resolveDuaNoAccess(isDealer, userId);
    }

    return resolveAccessResult(isCSC2User, parentAppName, userId, props.children);
};

AccessCheck.propTypes = {
    children: PropTypes.element.isRequired
};
